/* Builder steps that pick cards from the site's content: class and subclass (1),
   heritage (2) and domain cards (8). */

import { CSSProperties, useContext, useState } from 'react';
import { Plus } from 'lucide-react';
import { DomainIcon } from './cards/DomainIcon';
import { DOMAIN_COLORS } from './content/domains/data';
import { DomainCardSheet } from './cards/DomainCardSheet';
import { AncestrySheet } from './cards/AncestrySheet';
import { CommunitySheet } from './cards/CommunitySheet';
import { TransformationCard } from './cards/TransformationSheet';
import { sourceLabel } from './content/sources';
import { COMPANION_SHEET } from './content/sheetText';
import { classDomains, classFeatures, classTagline, ELEMENTS, featureList, hasElement, hasPatron } from './catalog';
import { ancestryFeatures, needs, withClass } from './model';

import { FeatureText } from './SheetParts';
import { CardBrowser, Chips, Choice, FeatureCard, Panel, Picker, setAt, ShowNeeded, StepProps, TextField, toggle, toggleExample } from './BuilderParts';

function SourceBadge({ source }: { source?: string }) {
  return source ? <span className="cb-source">{sourceLabel(source) === 'HF' ? 'Hope & Fear' : 'Void'}</span> : null;
}

export function ClassStep({ c, catalog, set }: StepProps) {
  const cls = catalog.cls(c.className);
  return (
    <>
      <Picker
        what="class"
        picked={!!cls}
        summary={cls && (
          <>
            <span className="cb-picked__icons" aria-hidden="true">
              {classDomains(cls).map((d) => <DomainIcon key={d} domain={d} className="cb-art" />)}
            </span>
            <span className="cb-picked__name">{cls.name}</span>
            <span className="cb-picked__meta">{cls.stats?.domains} · Evasion {cls.stats?.evasion} · HP {cls.stats?.hp}</span>
          </>
        )}
      >
        {(close) => (
          <div className="cb-grid cb-grid--classes">
            {catalog.classes.map((k) => (
              <Choice key={k.name} selected={k.name === c.className} onClick={() => { set(withClass(c, k.name)); close(); }} className="cb-class">
                <span className="cb-choice__title">{k.name}</span>
                <span className="cb-class__row">
                  <span className="cb-class__info">
                    <span className="cb-choice__meta">{k.stats?.domains}</span>
                    <span className="cb-class__stats">
                      <span>Evasion <strong>{k.stats?.evasion}</strong></span>
                      <span>HP <strong>{k.stats?.hp}</strong></span>
                    </span>
                    <SourceBadge source={k.source} />
                  </span>
                  <span className="cb-class__icons" aria-hidden="true" title={`${classDomains(k).join(' and ')} domains`}>
                    {classDomains(k).map((d) => <DomainIcon key={d} domain={d} className="cb-art" />)}
                  </span>
                </span>
              </Choice>
            ))}
          </div>
        )}
      </Picker>

      {cls && (
        <>
          <p className="cb-tagline">{classTagline(cls)}</p>
          <Panel title="Choose a subclass" cue={c.subclass ? undefined : `Pick a subclass to complete your ${cls.name}.`}>
            <div className="cb-grid cb-grid--two">
              {Object.values(cls.subclasses ?? {}).map((sub) => (
                <Choice
                  key={sub.name}
                  selected={sub.name === c.subclass}
                  onClick={() => set({ subclass: sub.name, stances: [] })}
                  className="cb-subclass"
                >
                  <span className="cb-choice__title">{sub.name}</span>
                  {sub.spellcastTrait && <span className="cb-choice__meta">Spellcast trait: {sub.spellcastTrait}</span>}
                  <span className="cb-choice__label">Foundation</span>
                  {featureList(sub.foundation).map((f, i) => (
                    <span key={f.name ?? i} className="cb-choice__text">
                      {f.name && <strong>{f.name}: </strong>}{f.description}
                    </span>
                  ))}
                </Choice>
              ))}
            </div>
          </Panel>
          <section className="cb-codex" aria-label={`What a ${cls.name} does`}>
            <h3 className="cb-step__title cb-codex__title">What a {cls.name} does</h3>
            <dl className="cb-codex__list">
              {cls.hopeFeature && (
                <div className="cb-codex__entry">
                  <dt>Hope Feature</dt>
                  <dd><FeatureText text={cls.hopeFeature} /></dd>
                </div>
              )}
              {classFeatures(cls).map((f, i) => (
                <div key={f.name ?? i} className="cb-codex__entry">
                  <dt>{f.name}</dt>
                  <dd>{f.description}</dd>
                </div>
              ))}
            </dl>
          </section>
          <ClassExtras c={c} catalog={catalog} set={set} />
        </>
      )}
    </>
  );
}

/** The few classes whose sheet asks for something more at creation. */
function ClassExtras({ c, catalog, set }: StepProps) {
  const cls = catalog.cls(c.className);
  const features = classFeatures(cls);
  const subclass = cls?.subclasses?.[c.subclass];
  const has = (name: string) => features.some((f) => f.name === name);

  return (
    <>
      <PatronPanel c={c} catalog={catalog} set={set} />
      <ElementPanel c={c} catalog={catalog} set={set} />
      {has('Strange Patterns') && (
        <Panel title="Strange Patterns">
          <p className="cb-hint">Choose a number between 1 and 12. When you roll that number on a Duality Die, gain a Hope or clear a Stress.</p>
          <Chips
            label="Strange Patterns number"
            options={Array.from({ length: 12 }, (_, i) => String(i + 1))}
            isOn={(n) => n === c.strangePattern}
            onPick={(strangePattern) => set({ strangePattern })}
          />
        </Panel>
      )}
      <StancePanel c={c} catalog={catalog} set={set} />
      {featureList(subclass?.foundation).some((f) => f.name === 'Companion') && (
        <Panel title="Your companion">
          <p className="cb-hint">{COMPANION_SHEET.intro}</p>
          <div className="cb-row">
            <TextField label="Companion name" value={c.companionName} onChange={(companionName) => set({ companionName })} />
            <TextField label="What animal are they?" value={c.companionAnimal} onChange={(companionAnimal) => set({ companionAnimal })} />
          </div>
          <div className="cb-row">
            {[0, 1].map((i) => (
              <TextField
                key={i}
                label={`Companion Experience ${i + 1} (+2)`}
                value={c.companionExperiences[i] ?? ''}
                onChange={(v) => set({ companionExperiences: setAt(c.companionExperiences, i, v) })}
              />
            ))}
          </div>
          <Chips
            label="Example Companion Experiences"
            options={COMPANION_SHEET.examples}
            isOn={(o) => c.companionExperiences.includes(o)}
            onPick={(o) => set({ companionExperiences: toggleExample(c.companionExperiences, o) })}
          />
        </Panel>
      )}
    </>
  );
}

const blurb = (item: { cardDescription?: string; description?: string[] }) => item.cardDescription ?? item.description?.[0];

export function HeritageStep({ c, catalog, set }: StepProps) {
  const ancestry = catalog.ancestry(c.ancestry);
  const community = catalog.community(c.community);
  return (
    <>
      <Panel title="Ancestry" needed={needs(c, 'ancestry')}>
        <Picker
          what="ancestry"
          picked={!!ancestry}
          summary={ancestry && (
            <>
              <span className="cb-picked__name">{[c.ancestry, c.mixedAncestry].filter(Boolean).join(' / ')}</span>
              <span className="cb-picked__meta">{blurb(ancestry)}</span>
            </>
          )}
        >
          {(close) => (
            <CardBrowser
              groups={[{ label: 'Ancestries', items: catalog.ancestries }]}
              name={(a) => a.name}
              card={(a) => <AncestrySheet ancestry={a} preview />}
              taken={(a) => a.name === c.ancestry}
              onTake={(a) => { set({ ancestry: a.name, mixedAncestry: c.mixedAncestry === a.name ? '' : c.mixedAncestry, purposeful: -1 }); close(); }}
            />
          )}
        </Picker>
        {ancestry && (
          <>
            <div className="cb-features">
              {ancestryFeatures(c, catalog).map((f) => <FeatureCard key={f.name} name={f.name}>{f.text}</FeatureCard>)}
            </div>
            <label className="cb-field cb-field--inline">
              <span className="cb-field__label">Mixed ancestry (optional)</span>
              <select value={c.mixedAncestry} onChange={(e) => set({ mixedAncestry: e.target.value, purposeful: -1 })}>
                <option value="">No, a single ancestry</option>
                {catalog.ancestries.filter((a) => a.name !== c.ancestry).map((a) => <option key={a.name} value={a.name}>{a.name}</option>)}
              </select>
            </label>
            <p className="cb-hint">To create a Mixed Ancestry, take the top (first-listed) feature from one ancestry and the bottom (second-listed) feature from another.</p>
          </>
        )}
      </Panel>
      <Panel title="Community" needed={needs(c, 'community')}>
        <Picker
          what="community"
          picked={!!community}
          summary={community && (
            <>
              <span className="cb-picked__name">{community.name}</span>
              <span className="cb-picked__meta">{blurb(community)}</span>
            </>
          )}
        >
          {(close) => (
            <CardBrowser
              groups={[{ label: 'Communities', items: catalog.communities }]}
              name={(k) => k.name}
              card={(k) => <CommunitySheet community={k} preview />}
              taken={(k) => k.name === c.community}
              onTake={(k) => { set({ community: k.name }); close(); }}
            />
          )}
        </Picker>
        {community && (
          <div className="cb-features">
            {(community.features ?? []).map((f) => <FeatureCard key={f.name} name={f.name}>{f.text}</FeatureCard>)}
          </div>
        )}
      </Panel>
      <TransformationPanel c={c} catalog={catalog} set={set} />
    </>
  );
}

/** The transformation cards; taking one replaces the old, taking it again gives it back. */
export function TransformationPicker({ c, catalog, set, onDone }: StepProps & { onDone?: () => void }) {
  return (
    <>
      <p className="cb-hint">You can only have one transformation per PC.</p>
      <CardBrowser
        groups={[{ label: 'Transformations', items: catalog.transformations }]}
        name={(t) => t.name}
        card={(t) => <TransformationCard t={t} />}
        taken={(t) => t.name === c.transformation}
        onTake={(t) => {
          set({ transformation: t.name === c.transformation ? '' : t.name });
          onDone?.();
        }}
      />
    </>
  );
}

/** Optional at creation, so it stays folded behind a button until the player asks for it. */
function TransformationPanel(props: StepProps) {
  const [adding, setAdding] = useState(false);
  const taken = props.catalog.transformation(props.c.transformation);
  return (
    <Panel title="Transformation">
      <p className="cb-hint">Optional. GMs may also present transformations as an option at character creation, at their discretion.</p>
      {taken || adding ? (
        <Picker
          what="transformation"
          picked={!!taken}
          summary={taken && (
            <>
              <span className="cb-picked__name">{taken.name}</span>
              <span className="cb-picked__meta">{taken.subtitle}</span>
            </>
          )}
        >
          {(close) => <TransformationPicker {...props} onDone={() => { close(); setAdding(false); }} />}
        </Picker>
      ) : (
        <button type="button" className="cb-btn" onClick={() => setAdding(true)}>
          <Plus size={16} aria-hidden="true" />Add a transformation
        </button>
      )}
    </Panel>
  );
}

export function DomainStep({ c, catalog, set }: StepProps) {
  const showNeeded = useContext(ShowNeeded);
  const domains = classDomains(catalog.cls(c.className));
  if (!domains.length) return <p className="cb-hint">Choose a class first: its two domains hold your cards.</p>;
  const cards = catalog.startingCards(domains);
  const full = c.domainCards.length >= 2;
  const lit = showNeeded && !full;
  const domainColor = (d: string) => ({ '--domain-color': DOMAIN_COLORS[d] }) as CSSProperties;
  return (
    <>
      <p className={`cb-counter${lit ? ' cb-counter--needed' : ''}`} aria-live="polite">{c.domainCards.length} of 2 cards chosen</p>
      <CardBrowser
        groups={domains.map((d) => ({
          label: d,
          style: domainColor(d),
          head: (
            <h3 className="cb-domain__title">
              <DomainIcon domain={d} className="cb-art" />
              <span className={`domain-badge domain-${d.toLowerCase()}`}>{d}</span>
            </h3>
          ),
          items: cards.filter((k) => k.domain === d),
        }))}
        name={(k) => k.name}
        card={(k) => (
          <div className={`cb-deal${c.domainCards.includes(k.name) ? ' is-selected' : ''}`} style={domainColor(k.domain)}>
            <DomainCardSheet card={k} />
          </div>
        )}
        taken={(k) => c.domainCards.includes(k.name)}
        canTake={!full}
        onTake={(k) => set({ domainCards: toggle(c.domainCards, k.name) })}
      />
    </>
  );
}

/** Stance Fighter's two Tier 1 stances, required at creation (Detailed class step and Quick builder alike). */
export function StancePanel({ c, catalog, set }: StepProps) {
  const stances = catalog.cls(c.className)?.subclasses?.[c.subclass]?.martialForm?.tiers['1'] ?? [];
  if (!stances.length) return null;
  return (
    <Panel title="Martial stances" needed={needs(c, 'two martial stances')}>
      <p className="cb-hint">Choose two martial stances from Tier 1. They are marked as known on your Martial Stances sheet.</p>
      <div className="cb-grid cb-grid--two">
        {stances.map((s) => {
          const on = c.stances.includes(s.name);
          return (
            <Choice key={s.name} selected={on} disabled={!on && c.stances.length >= 2} onClick={() => set({ stances: toggle(c.stances, s.name) })}>
              <span className="cb-choice__title">{s.name}</span>
              <span className="cb-choice__text">{s.description}</span>
            </Choice>
          );
        })}
      </div>
    </Panel>
  );
}

/** The spheres of influence Patron's Pact names as examples. */
const SPHERES = ['Nature', 'Chaos', 'Wisdom', 'Mischief', 'Love', 'War', 'Justice', 'Death'];

/** Patron's Pact: the patron's name and sphere of influence, each capped to fit its line on the sheet. */
export function PatronFields({ c, set }: Pick<StepProps, 'c' | 'set'>) {
  return (
    <>
      <p className="cb-hint">Write down your patron's name, then work with your GM to determine their sphere of influence. You can change both any time from your sheet.</p>
      <div className="cb-row">
        <TextField label="Patron's name" value={c.patronName} maxLength={28} onChange={(patronName) => set({ patronName })} />
        <TextField label="Sphere of influence" value={c.sphere} maxLength={28} onChange={(sphere) => set({ sphere })} />
      </div>
      <Chips label="Example spheres of influence" options={SPHERES} isOn={(o) => o === c.sphere} onPick={(sphere) => set({ sphere })} />
    </>
  );
}

/** The patron at creation, in the Detailed class step and on the Quick page; optional, so it can wait for the GM. */
export function PatronPanel({ c, catalog, set }: StepProps) {
  if (!hasPatron(catalog.cls(c.className))) return null;
  return (
    <Panel title="Your patron">
      <PatronFields c={c} set={set} />
    </Panel>
  );
}

/** Elementalist: the element, picked from the SRD's five (tap the picked one again to clear it). */
export function ElementFields({ c, set }: Pick<StepProps, 'c' | 'set'>) {
  return (
    <>
      <p className="cb-hint">Choose one of the following elements at character creation: air, earth, fire, lightning, water. You can change it any time from your subclass card.</p>
      <Chips label="Elements" options={ELEMENTS} isOn={(o) => o === c.element} onPick={(o) => set({ element: o === c.element ? '' : o })} />
    </>
  );
}

/** The element at creation, in the Detailed class step and on the Quick page, for an Elemental Origin Sorcerer. */
export function ElementPanel({ c, catalog, set }: StepProps) {
  if (!hasElement(catalog.cls(c.className), c.subclass)) return null;
  return (
    <Panel title="Your element">
      <ElementFields c={c} set={set} />
    </Panel>
  );
}
